import { ReactFlowProvider } from 'reactflow';
import { Header } from './components/Header';
import { DropZone } from './components/DropZone';
import { FlowGraph } from './components/FlowGraph';
import { DetailPanel } from './components/DetailPanel';
import { SkeletonGraph } from './components/LoadingSpinner';
import { NoNodesEmptyState } from './components/EmptyState';
import { useStore } from './store/useStore';
import { Shield } from 'lucide-react';

function App() {
  const { workflows, getCurrentWorkflow, isLoading } = useStore();
  const currentWorkflow = getCurrentWorkflow();
  const hasWorkflows = workflows.length > 0;

  return (
    <div className="h-screen flex flex-col bg-background text-foreground">
      <Header />

      <main className="flex-1 overflow-auto relative">
        {!hasWorkflows ? (
          // Landing Page - No workflow loaded
          <div className="min-h-full flex flex-col items-center justify-center p-8 pt-16">
            <div className="text-center max-w-3xl mx-auto mb-8 animate-fade-in">
              <h2 className="text-4xl font-bold mb-4 text-foreground">
                Privacy-First Zapier Workflow Viewer
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Visualize and analyze your Zapier workflows without sending data to any
                server. Everything happens right here in your browser.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span>Your data never leaves your device</span>
              </div>
            </div>

            <DropZone />

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
              <div className="text-center p-6 bg-card rounded-lg border border-border">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">100% Client-Side</h3>
                <p className="text-sm text-muted-foreground">
                  No file uploads. All parsing happens in your browser using the FileReader
                  API.
                </p>
              </div>

              <div className="text-center p-6 bg-card rounded-lg border border-border">
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg
                    className="w-6 h-6 text-blue-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Free Graph View</h3>
                <p className="text-sm text-muted-foreground">
                  Visualize your workflow structure, see app connections, and understand
                  the flow.
                </p>
              </div>

              <div className="text-center p-6 bg-card rounded-lg border border-border">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg
                    className="w-6 h-6 text-amber-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Unlock Details</h3>
                <p className="text-sm text-muted-foreground">
                  Get a license to view step configurations, mapped fields, and code
                  snippets.
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Workflow View
          <div className="h-full relative">
            {isLoading ? (
              // Show skeleton while loading/processing
              <SkeletonGraph />
            ) : currentWorkflow ? (
              currentWorkflow.nodes.length === 0 ? (
                // Empty workflow (no nodes)
                <NoNodesEmptyState />
              ) : (
                // Normal workflow with nodes
                <ReactFlowProvider>
                  <FlowGraph />
                </ReactFlowProvider>
              )
            ) : (
              // Multiple workflows but none selected
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <p>Select a workflow to view</p>
              </div>
            )}

            {/* Detail Panel Overlay - only show when not loading and has nodes */}
            {!isLoading && currentWorkflow && currentWorkflow.nodes.length > 0 && <DetailPanel />}
          </div>
        )}
      </main>

      {/* Footer */}
      {hasWorkflows && currentWorkflow && (
        <footer className="border-t border-border bg-card/50 backdrop-blur-sm px-4 py-2 text-xs text-muted-foreground flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>
              {currentWorkflow.nodes.length} step
              {currentWorkflow.nodes.length !== 1 ? 's' : ''}
            </span>
            {currentWorkflow.status && (
              <span
                className={
                  currentWorkflow.status === 'on'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : ''
                }
              >
                Status: {currentWorkflow.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3 text-emerald-500" />
            <span>Processing locally</span>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
